import { useCallback, useMemo } from "react";
import { Divider, Grid, Typography, Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { SubmitButton } from "@avenue-8/sidekick-ui";
import { UserBrandingForm, UserBrandingFormProps } from "./UserBrandingForm";
import { useAuthStore } from "@store/auth.store";
import { useGetUserBranding } from "@queries/profile/useGetUserBranding";
import { useUpsertUserBranding } from "@queries/profile/useUpsertUserBranding";
import { UserBrandingSchema } from "./UserBrandingForm/user-branding-form-helpers";

export const UserBranding = () => {
  const user = useAuthStore((state) => state.user);

  const userBrandingQuery = useGetUserBranding({
    enabled: true,
  });

  const upsertUserBrandingMutation = useUpsertUserBranding();

  const upsertUserBranding = (data: UserBrandingSchema) => {
    upsertUserBrandingMutation.mutate(data);
  };

  const userBranding: UserBrandingFormProps["defaultValues"] | null =
    useMemo(() => {
      if (!userBrandingQuery.data) {
        return null;
      }

      return {
        agentName: userBrandingQuery.data.agentName || null,
        agentEmail: userBrandingQuery.data.agentEmail || null,
        agentTitle: userBrandingQuery.data.agentTitle || null,
        agentAvatar: userBrandingQuery.data.agentAvatar || null,
        agentMlsId: userBrandingQuery.data.agentMlsId || null,
        agentPhoneNumber: userBrandingQuery.data.agentPhoneNumber || null,
        officeName: userBrandingQuery.data.officeName || null,
        officeLogo: userBrandingQuery.data.officeLogo || null,
        website: userBrandingQuery.data.website || null,
        teamName: userBrandingQuery.data.teamName || null,
        teamLogo: userBrandingQuery.data.teamLogo || null,
        instagramUrl: userBrandingQuery.data.instagramUrl || null,
        facebookUrl: userBrandingQuery.data.facebookUrl || null,
        xUrl: userBrandingQuery.data.xUrl || null,
        youtubeUrl: userBrandingQuery.data.youtubeUrl || null,
        linkedInUrl: userBrandingQuery.data.linkedInUrl || null,
      };
    }, [userBrandingQuery.data]);

  const handleClickAddBranding = useCallback(async () => {
    await upsertUserBrandingMutation.mutateAsync({
      agentName: user.name || null,
      agentAvatar: user.avatar || null,
      agentEmail: user.email || null,
      agentMlsId: user.mlsId || null,
      agentPhoneNumber: user.phoneNumber || null,
      agentTitle: "Real Estate Agent",
      officeName: user.officeName || null,
    });
  }, [upsertUserBrandingMutation, user]);

  return (
    <>
      <Divider sx={{ mt: 4, mb: 3 }} />
      <Grid container>
        <Grid item xs={12} md={9} mb={3}>
          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            Branding Information
          </Typography>

          <Typography variant="body2">
            <strong>Note:</strong> The information you provide here may be
            publicly displayed in various places, such as your CMA
            presentations, profile pages, or shared materials. Make sure
            it&apos;s accurate and professional.
          </Typography>
        </Grid>

        {!userBrandingQuery.isPending && !userBranding && (
          <Grid item xs={12} md={9} mb={3}>
            <Box>
              <SubmitButton
                variant="outlined"
                type="button"
                startIcon={<AddIcon />}
                onClick={handleClickAddBranding}
                loadingText="Adding..."
                isLoading={upsertUserBrandingMutation.isPending}
              >
                Add Branding
              </SubmitButton>
            </Box>
          </Grid>
        )}

        {!userBrandingQuery.isPending && !!userBranding && (
          <UserBrandingForm
            defaultValues={userBranding}
            upsertUserBranding={upsertUserBranding}
            isUpsertUserBrandingPending={upsertUserBrandingMutation.isPending}
          />
        )}
      </Grid>
    </>
  );
};
